import ConfirmDeactivation from "./ConfirmDeactivation";
import EntityStatus from "./EntityStatus";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { readErrorMessage } from "../lib/http";
import { Workstation, WorkstationTag } from "../types/clientCompanies";
import GenericTable, { GenericTableColumn } from "./GenericTable";
import Modal from "./Modal";
import WorkstationTagsManager from "./WorkstationTagsManager";

type WorkstationsManagerProps = {
  clientCompanyId: string;
  authorizedFetch: (path: string, init?: RequestInit) => Promise<Response>;
  canManageClientCompanies: boolean;
};

type WorkstationFormValues = {
  name: string;
  contactEmail: string;
  tagIds: string[];
};

const emptyValues: WorkstationFormValues = {
  name: "",
  contactEmail: "",
  tagIds: [],
};

const WORKSTATION_FORM_ID = "workstation-form";

function WorkstationsManager({
  clientCompanyId,
  authorizedFetch,
  canManageClientCompanies,
}: WorkstationsManagerProps) {
  const [pendingDeactivation, setPendingDeactivation] =
    useState<Workstation | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [tags, setTags] = useState<WorkstationTag[]>([]);
  const [values, setValues] = useState<WorkstationFormValues>(emptyValues);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const endpoint = `/client-companies/${clientCompanyId}/workstations`;

  const loadWorkstations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authorizedFetch(
        `${endpoint}?includeInactive=true`,
      );

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load workstations (${response.status}).`,
          ),
        );
      }

      setWorkstations((await response.json()) as Workstation[]);
    } catch (loadError) {
      const message =
        loadError instanceof Error
          ? loadError.message
          : "Failed to load workstations.";
      setError(message);
      setWorkstations([]);
    } finally {
      setIsLoading(false);
    }
  }, [authorizedFetch, endpoint]);

  const loadTags = useCallback(async () => {
    try {
      const response = await authorizedFetch(
        `/client-companies/${clientCompanyId}/tags`,
      );

      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load tags (${response.status}).`,
          ),
        );
      }

      setTags((await response.json()) as WorkstationTag[]);
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : "Failed to load tags.",
      );
      setTags([]);
    }
  }, [authorizedFetch, clientCompanyId]);

  // Renaming or deleting a tag changes the tags shown on workstations too.
  const onTagsChanged = useCallback(async () => {
    await Promise.all([loadTags(), loadWorkstations()]);
  }, [loadTags, loadWorkstations]);

  useEffect(() => {
    void loadWorkstations();
  }, [loadWorkstations]);

  useEffect(() => {
    void loadTags();
  }, [loadTags]);

  const openAddModal = () => {
    setEditingId(null);
    setValues(emptyValues);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (workstation: Workstation) => {
    setEditingId(workstation.id);
    setValues({
      name: workstation.name,
      contactEmail: workstation.contactEmail ?? "",
      tagIds: workstation.tags?.map((tag) => tag.id) ?? [],
    });
    setFormError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setValues(emptyValues);
    setFormError(null);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canManageClientCompanies) {
      setFormError("Only manager/admin users can manage workstations.");
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    const payload = {
      name: values.name.trim(),
      contactEmail: values.contactEmail.trim() || null,
      tagIds: values.tagIds,
    };

    try {
      const response = await authorizedFetch(
        editingId ? `${endpoint}/${editingId}` : endpoint,
        {
          method: editingId ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const fallbackMessage =
          response.status === 409
            ? "A workstation with this name already exists for this company."
            : response.status === 400
              ? "Enter a workstation name and, if given, a valid contact email."
              : `Failed to save workstation (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      const savedMessage = editingId
        ? "Workstation updated."
        : "Workstation added.";
      setIsModalOpen(false);
      setEditingId(null);
      setValues(emptyValues);
      toast.success(savedMessage);
      await loadWorkstations();
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message
          : "Failed to save workstation.";
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async (workstation: Workstation) => {
    if (!canManageClientCompanies) {
      setError("Only manager/admin users can manage workstations.");
      return;
    }

    setPendingDeactivation(null);
    setChangingStatus(true);
    setError(null);

    try {
      const response = await authorizedFetch(
        `${endpoint}/${workstation.id}${workstation.deletedAt ? "/reactivate" : ""}`,
        {
          method: workstation.deletedAt ? "POST" : "DELETE",
        },
      );

      if (!response.ok) {
        const fallbackMessage = `Failed to ${workstation.deletedAt ? "reactivate" : "deactivate"} workstation (${response.status}).`;

        throw new Error(await readErrorMessage(response, fallbackMessage));
      }

      toast.success(
        workstation.deletedAt
          ? "Workstation reactivated."
          : "Workstation deactivated.",
      );
      await loadWorkstations();
    } catch (deleteError) {
      const message =
        deleteError instanceof Error
          ? deleteError.message
          : `Failed to ${workstation.deletedAt ? "reactivate" : "deactivate"} workstation.`;
      setError(message);
    } finally {
      setChangingStatus(false);
    }
  };

  const columns: GenericTableColumn<Workstation>[] = [
    {
      id: "name",
      header: "Name",
      cell: (workstation) => (
        <>
          {workstation.name} <EntityStatus deletedAt={workstation.deletedAt} />
        </>
      ),
    },
    {
      id: "contactEmail",
      header: "Contact Email",
      cell: (workstation) => workstation.contactEmail ?? "-",
    },
    {
      id: "tags",
      header: "Tags",
      cell: (workstation) =>
        workstation.tags?.length
          ? workstation.tags.map((tag) => tag.name).join(", ")
          : "-",
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "actionsColumn",
      cellClassName: "actionsColumn",
      cell: (workstation) => (
        <div className="headerActions">
          <button
            className="secondaryButton tableActionButton"
            type="button"
            onClick={() => openEditModal(workstation)}
            disabled={!canManageClientCompanies}
          >
            Edit
          </button>
          <button
            className="secondaryButton tableActionButton"
            type="button"
            key={workstation.deletedAt ? "reactivate" : "deactivate"}
            onClick={() =>
              workstation.deletedAt
                ? void onDelete(workstation)
                : setPendingDeactivation(workstation)
            }
            disabled={!canManageClientCompanies || changingStatus}
          >
            {workstation.deletedAt ? "Reactivate" : "Deactivate"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <section
        className="subCard workstationsSection"
        aria-labelledby="workstations-heading"
      >
        <ConfirmDeactivation
          isOpen={pendingDeactivation !== null}
          onCancel={() => setPendingDeactivation(null)}
          onConfirm={() => {
            if (pendingDeactivation) void onDelete(pendingDeactivation);
          }}
        />
        <div className="cardHeaderRow">
          <div>
            <h3 className="subCardTitle" id="workstations-heading">
              Workstations
            </h3>
            <p className="hintText sectionHint">
              Add and maintain workstation contact points for this company.
            </p>
          </div>

          <button
            className="primaryButton"
            type="button"
            onClick={openAddModal}
            disabled={!canManageClientCompanies}
          >
            Add workstation
          </button>
        </div>

        {error ? (
          <p className="errorMessage" role="alert">
            {error}
          </p>
        ) : null}

        {isLoading ? <p>Loading workstations...</p> : null}

        {!isLoading && workstations.length === 0 ? (
          <p className="hintText">No workstations have been added yet.</p>
        ) : null}

        {!isLoading && workstations.length > 0 ? (
          <GenericTable
            ariaLabel="Workstations"
            columns={columns}
            rows={workstations}
            rowKey={(workstation) => workstation.id}
          />
        ) : null}

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          titleId="workstation-modal-title"
          title={editingId ? "Edit workstation" : "Add workstation"}
          description={
            editingId
              ? "Update the workstation name or contact email."
              : "Create a new workstation contact point for this company."
          }
        >
          <form
            id={WORKSTATION_FORM_ID}
            className="modalForm"
            onSubmit={onSubmit}
            noValidate
          >
            <label className="field">
              <span className="fieldLabel">Name</span>
              <input
                className="inputControl"
                value={values.name}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </label>

            <label className="field">
              <span className="fieldLabel">Contact Email</span>
              <input
                className="inputControl"
                type="email"
                value={values.contactEmail}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    contactEmail: event.target.value,
                  }))
                }
              />
            </label>

            {tags.length > 0 ? (
              <fieldset className="field tagFieldset">
                <legend className="fieldLabel">Tags</legend>
                {tags.map((tag) => (
                  <label className="checkboxRow" key={tag.id}>
                    <input
                      type="checkbox"
                      checked={values.tagIds.includes(tag.id)}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          tagIds: event.target.checked
                            ? [...current.tagIds, tag.id]
                            : current.tagIds.filter((id) => id !== tag.id),
                        }))
                      }
                    />
                    <span className="checkboxRowText">{tag.name}</span>
                  </label>
                ))}
              </fieldset>
            ) : null}

            {formError ? (
              <p className="errorMessage" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="modalFooter">
              <button
                className="secondaryButton"
                type="button"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="primaryButton"
                type="submit"
                disabled={isSubmitting || !canManageClientCompanies}
              >
                {isSubmitting
                  ? "Saving..."
                  : editingId
                    ? "Save changes"
                    : "Add workstation"}
              </button>
            </div>
          </form>
        </Modal>
      </section>

      <WorkstationTagsManager
        clientCompanyId={clientCompanyId}
        tags={tags}
        authorizedFetch={authorizedFetch}
        canManageClientCompanies={canManageClientCompanies}
        onChanged={onTagsChanged}
      />
    </>
  );
}

export default WorkstationsManager;
